import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

type Identifiable = { id: number };
type CountMeta = {
  count: number;
  page: number;
  pages: number;
  next: string | null;
  previous: string | null;
};
type InfinitePage<TItem extends Identifiable> = {
  results: TItem[];
  meta: CountMeta;
};

type RealtimeInfiniteListCacheOptions<TParams> = {
  queryClient: QueryClient;
  queryRoot: QueryKey;
  getQueryKey: (params: TParams) => QueryKey;
};

type VisibleListState<TItem> = {
  ids: number[];
  objects: TItem[];
  pageSize: number;
  loadedPages: number;
  filteredCount: number;
  hasMore: boolean;
};

const REALTIME_MERGE_MARKER = Symbol('realtimeMerge');
type MarkedInfiniteData<TPage> = InfiniteData<TPage> & {
  [REALTIME_MERGE_MARKER]?: true;
};

function preserveRetainedRowsInRefetchedData<
  TItem extends Identifiable,
  TPage extends InfinitePage<TItem>,
>(
  oldData: unknown,
  newData: InfiniteData<TPage>,
  retainedIds: number[],
): InfiniteData<TPage> {
  const markedNewData = newData as MarkedInfiniteData<TPage>;
  if (markedNewData[REALTIME_MERGE_MARKER]) {
    const cleanNewData = { ...markedNewData };
    delete cleanNewData[REALTIME_MERGE_MARKER];
    return cleanNewData;
  }

  if (
    !isInfiniteListData<TPage>(oldData) ||
    !oldData.pages.length ||
    !newData.pages.length ||
    !retainedIds.length
  ) {
    return newData;
  }

  const retainedIdSet = new Set(retainedIds);
  const oldItems = oldData.pages.flatMap((page) => page.results);
  const newItems = newData.pages.flatMap((page) => page.results);
  const newItemsById = new Map(newItems.map((item) => [item.id, item]));
  const seenRetainedIds = new Set<number>();
  const retainedRows = oldItems.flatMap((oldItem, oldIndex) => {
    if (!retainedIdSet.has(oldItem.id) || seenRetainedIds.has(oldItem.id)) {
      return [];
    }
    seenRetainedIds.add(oldItem.id);
    return [
      {
        index: oldIndex,
        item: newItemsById.get(oldItem.id) ?? oldItem,
      },
    ];
  });
  if (!retainedRows.length) return newData;

  const mergedItems = newItems.filter((item) => !retainedIdSet.has(item.id));
  retainedRows.forEach(({ index, item }) => {
    mergedItems.splice(Math.min(index, mergedItems.length), 0, item);
  });

  let offset = 0;
  const lastPageIndex = newData.pages.length - 1;
  return {
    ...newData,
    pages: newData.pages.map((page, pageIndex) => {
      const end =
        pageIndex === lastPageIndex
          ? mergedItems.length
          : offset + page.results.length;
      const nextPage = {
        ...page,
        results: mergedItems.slice(offset, end),
      } as TPage;
      offset = end;
      return nextPage;
    }),
  };
}

function isInfiniteListData<TPage>(data: unknown): data is InfiniteData<TPage> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'pages' in data &&
    Array.isArray(data.pages)
  );
}

/** Immutable cache operations shared by realtime paginated lists. */
export function createRealtimeInfiniteListCache<
  TItem extends Identifiable,
  TPage extends InfinitePage<TItem>,
  TParams,
>({
  queryClient,
  queryRoot,
  getQueryKey,
}: RealtimeInfiniteListCacheOptions<TParams>) {
  const updateManyPreservingPosition = (items: TItem[]) => {
    const itemsById = new Map(items.map((item) => [item.id, item]));
    if (itemsById.size === 0) return;
    queryClient.setQueriesData(
      { queryKey: queryRoot, exact: false },
      (oldData) => {
        if (!isInfiniteListData<TPage>(oldData)) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            results: page.results.map((item) => itemsById.get(item.id) ?? item),
          })),
          [REALTIME_MERGE_MARKER]: true,
        };
      },
    );
  };

  return {
    preserveRetainedRows(
      oldData: unknown,
      newData: unknown,
      retainedIds: number[],
    ) {
      if (!isInfiniteListData<TPage>(newData)) return newData;
      return preserveRetainedRowsInRefetchedData(oldData, newData, retainedIds);
    },
    updateOnePreservingPosition(item: TItem) {
      updateManyPreservingPosition([item]);
    },
    prependNew(params: TParams, item: TItem) {
      let inserted = false;
      queryClient.setQueryData<InfiniteData<TPage>>(
        getQueryKey(params),
        (oldData) => {
          if (!oldData) return oldData;
          if (
            oldData.pages.some((page) =>
              page.results.some(({ id }) => id === item.id),
            )
          ) {
            return oldData;
          }
          inserted = true;
          return {
            ...oldData,
            pages: oldData.pages.map((page, index) => ({
              ...page,
              meta: { ...page.meta, count: page.meta.count + 1 },
              results: index === 0 ? [item, ...page.results] : page.results,
            })) as TPage[],
            [REALTIME_MERGE_MARKER]: true,
          };
        },
      );
      return inserted;
    },
    mergeVisibleState(params: TParams, state: VisibleListState<TItem>) {
      let merged = false;
      queryClient.setQueryData<InfiniteData<TPage>>(
        getQueryKey(params),
        (oldData) => {
          if (!oldData?.pages.length) return oldData;
          const objectsById = new Map(
            oldData.pages
              .flatMap((page) => page.results)
              .map((item) => [item.id, item]),
          );
          state.objects.forEach((item) => {
            objectsById.set(item.id, item);
          });
          const orderedObjects = state.ids.flatMap((id) => {
            const item = objectsById.get(id);
            return item ? [item] : [];
          });
          const pageCount = Math.max(1, state.loadedPages);
          const totalPages = Math.max(
            1,
            Math.ceil(state.filteredCount / state.pageSize),
          );
          const lastTemplate = oldData.pages.at(-1);
          if (!lastTemplate) return oldData;

          const pages = Array.from({ length: pageCount }, (_, index) => {
            const template = oldData.pages[index] ?? lastTemplate;
            const start = index * state.pageSize;
            const end =
              index === pageCount - 1
                ? orderedObjects.length
                : start + state.pageSize;
            return {
              ...template,
              meta: {
                ...template.meta,
                page: index + 1,
                pages: totalPages,
                count: state.filteredCount,
                next:
                  index < pageCount - 1 || state.hasMore
                    ? 'visible-list'
                    : null,
                previous: index > 0 ? 'visible-list' : null,
              },
              results: orderedObjects.slice(start, end),
            } as TPage;
          });
          merged = true;
          return {
            ...oldData,
            pages,
            pageParams: Array.from(
              { length: pageCount },
              (_, index) => index + 1,
            ),
            [REALTIME_MERGE_MARKER]: true,
          };
        },
      );
      return merged;
    },
  };
}
